import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  MapPin,
  FileText,
  HelpCircle,
  CheckCircle2,
  Euro,
  TreePine,
} from "lucide-react";
import geothermVideo from "../assets/geotherm_start_vid.mp4";

export default function SelectionPage() {
  useEffect(() => {
    document.title =
      "Erdwärme Grundstück prüfen | GeoReport für flache Geothermie";

    let meta = document.querySelector("meta[name='description']");
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "description";
      document.head.appendChild(meta);
    }
    meta.setAttribute(
      "content",
      "Prüfen Sie online, ob Erdwärme auf Ihrem Grundstück sinnvoll ist. Ersteinschätzung zu flacher Geothermie, Boden, Erdkollektoren, Erdsonden, Kosten und Förderung."
    );

    let canonical = document.querySelector("link[rel='canonical']");
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.href = "https://www.erdwaerme-experten.de/";
  }, []);

  const features = [
    {
      title: "Flache Geothermie verstehen",
      description:
        "Grundlagen, Vorteile, Systemarten und typische Einsatzmöglichkeiten von Erdkollektoren und Erdsonden verständlich erklärt.",
      icon: <MapPin className="w-12 h-12 text-white" />,
      route: "/flache-geothermie",
      color: "from-red-600 to-orange-500",
      button: "Grundlagen ansehen",
    },
    {
      title: "GeoReport für Ihr Grundstück",
      description:
        "Datenbasierte Ersteinschätzung zu Boden, Wärmeleitfähigkeit, Entzugsleistung, Wirtschaftlichkeit und Fördermöglichkeiten.",
      icon: <FileText className="w-12 h-12 text-white" />,
      route: "/report",
      color: "from-amber-500 to-red-500",
      button: "Grundstück prüfen",
    },
    {
      title: "Fragen & Antworten",
      description:
        "Antworten auf häufige Fragen zur Eignung von Grundstücken, zu Kosten, Genehmigungen und zum Ablauf des GeoReports.",
      icon: <HelpCircle className="w-12 h-12 text-white" />,
      route: "/faq",
      color: "from-gray-700 to-red-600",
      button: "FAQ öffnen",
    },
  ];

  const benefits = [
    {
      icon: <CheckCircle2 className="w-6 h-6 text-red-600" />,
      title: "Schnelle Ersteinschätzung",
      text: "Erhalten Sie eine erste datenbasierte Einschätzung, ob flache Geothermie für Ihr Grundstück grundsätzlich sinnvoll sein kann.",
    },
    {
      icon: <Euro className="w-6 h-6 text-red-600" />,
      title: "Kosten & Wirtschaftlichkeit",
      text: "Vergleichen Sie typische Investitionen und laufende Kosten verschiedener Heizsysteme als Orientierung für Ihre Entscheidung.",
    },
    {
      icon: <TreePine className="w-6 h-6 text-red-600" />,
      title: "Standortbezogene Daten",
      text: "Je nach Region werden öffentlich verfügbare Boden- und Standortinformationen für eine erste geothermische Bewertung herangezogen.",
    },
  ];

  return (
    <div className="flex flex-col min-h-screen bg-gradient-to-b from-white to-[#f6f3f2]">
      {/* Hero */}
      <section className="relative min-h-[100svh] md:min-h-[88vh] w-full overflow-hidden">
        <video
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover"
        >
          <source src={geothermVideo} type="video/mp4" />
          Dein Browser unterstützt keine Videos.
        </video>

        <div className="absolute inset-0 bg-black/55" />

        <div className="relative z-10 max-w-6xl mx-auto px-6 min-h-[88vh] flex items-center">
          <div className="max-w-3xl text-white">
            <p className="uppercase tracking-[0.12em] md:tracking-[0.2em] text-xs sm:text-sm md:text-base font-semibold text-red-200 mb-3 md:mb-4">
			  Nachhaltig heizen mit Geothermie
			</p>

			<h1 className="text-3xl md:text-6xl font-extrabold leading-tight">
			  Erdwärme auf Ihrem Grundstück prüfen
			</h1>

			<p className="block md:hidden mt-4 text-base text-white/95 leading-relaxed max-w-xl">
			  Der GeoReport liefert eine erste standortbezogene Einschätzung zu
			  Erdwärme, Boden, Kosten und Eignung.
			</p>

			<p className="hidden md:block mt-6 text-lg md:text-2xl text-white/95 leading-relaxed max-w-2xl">
			  Mit dem GeoReport erhalten Sie eine erste datenbasierte
			  Einschätzung zur Eignung Ihres Grundstücks für flache Geothermie –
			  inklusive Bodenbewertung, typischer Systemoptionen, Kosten und
			  Wirtschaftlichkeit.
			</p>

            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <Link
                to="/report"
                className="px-7 py-3.5 bg-red-600 text-center text-white rounded-full text-lg font-semibold shadow-lg hover:bg-red-700 transition"
              >
                Grundstück jetzt prüfen
              </Link>

              <Link
                to="/flache-geothermie"
                className="px-7 py-3.5 bg-white/15 backdrop-blur text-center text-white border border-white/40 rounded-full text-lg font-semibold hover:bg-white/25 transition"
              >
                Mehr über flache Geothermie
              </Link>
            </div>

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm md:text-base">
              <div className="bg-white/10 backdrop-blur rounded-xl px-4 py-3 border border-white/15">
                Erste Orientierung für Eigentümer und Bauherren
              </div>
              <div className="bg-white/10 backdrop-blur rounded-xl px-4 py-3 border border-white/15">
                Standortbezogene Auswertung öffentlich verfügbarer Daten
              </div>
              <div className="bg-white/10 backdrop-blur rounded-xl px-4 py-3 border border-white/15">
                Verständliche Einordnung von Kosten und Potenzial
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="bg-white w-full shadow-sm">
        <div className="max-w-5xl mx-auto px-6 py-16 text-center">
          <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 tracking-tight">
            Der digitale Einstieg in die flache Geothermie
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto mt-5 leading-relaxed">
            Erdwärme ist eine langfristig interessante Option für die
            Wärmeversorgung von Wohngebäuden. Ob ein Grundstück grundsätzlich
            geeignet ist, hängt jedoch von mehreren Faktoren ab – zum Beispiel
            von Bodenverhältnissen, verfügbarer Fläche, Wärmebedarf und dem
            geplanten System. Genau hier setzt der GeoReport an.
          </p>
          <div className="mt-5 inline-block w-24 h-1 bg-red-600 rounded-full" />
        </div>
      </section>

      {/* Benefit Section */}
      <section className="max-w-6xl mx-auto px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {benefits.map((item, idx) => (
            <div
              key={idx}
              className="bg-white rounded-2xl border border-gray-200 shadow-sm p-7"
            >
              <div className="mb-4">{item.icon}</div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                {item.title}
              </h3>
              <p className="text-gray-600 leading-relaxed">{item.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Feature Cards */}
      <section className="max-w-6xl mx-auto px-6 pb-20">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900">
            Ihre nächsten Schritte
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto mt-4">
            Informieren Sie sich über die Grundlagen oder starten Sie direkt mit
            einer ersten Einschätzung für Ihr Grundstück.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
          {features.map((f, idx) => (
            <Link
              key={idx}
              to={f.route}
              className="bg-white border border-gray-200 rounded-2xl shadow-md hover:shadow-xl transition transform hover:-translate-y-1 hover:border-red-500 p-8 flex flex-col"
            >
              <div
                className={`bg-gradient-to-r ${f.color} w-fit p-5 rounded-full mb-6 shadow-sm`}
              >
                {f.icon}
              </div>

              <h3 className="text-xl font-bold mb-3 text-gray-900">
                {f.title}
              </h3>

              <p className="text-gray-600 mb-6 leading-relaxed flex-grow">
                {f.description}
              </p>

              <span className="self-start px-6 py-2.5 bg-red-600 text-white rounded-full shadow hover:bg-red-700 transition font-medium">
                {f.button}
              </span>
            </Link>
          ))}
        </div>
      </section>

      {/* SEO / Info Text */}
      <section className="bg-white border-t border-gray-200">
        <div className="max-w-5xl mx-auto px-6 py-16">
          <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-6">
            Worum geht es beim GeoReport?
          </h2>

          <div className="space-y-5 text-gray-700 text-lg leading-relaxed">
            <p>
              Der GeoReport ist eine digitale Ersteinschätzung für die Nutzung
              von flacher Geothermie auf einem konkreten Grundstück. Er richtet
              sich insbesondere an Eigentümer, Bauherren und Sanierungsinteressierte,
              die vorab besser verstehen möchten, ob Erdwärme grundsätzlich eine
              sinnvolle Option sein kann.
            </p>

            <p>
              Dafür werden standortbezogene Informationen mit typischen
              Annahmen zur Gebäude- und Wärmesituation kombiniert. Das Ergebnis
              ist keine Detailplanung und ersetzt kein Fachgutachten, bietet
              aber eine fundierte Orientierung zu Systemwahl, Randbedingungen,
              Kosten und Wirtschaftlichkeit.
            </p>

            <p>
              Wenn Sie also wissen möchten, ob Erdkollektoren oder Erdsonden für
              Ihr Grundstück grundsätzlich infrage kommen könnten, erhalten Sie
              mit dem GeoReport einen schnellen und verständlichen Einstieg in
              das Thema.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}